import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Upload, 
  FileText, 
  Trash2, 
  Download, 
  Loader2,
  File,
  FileImage,
  FileArchive,
  Eye,
  Sparkles,
  SplitSquareVertical,
  User
} from "lucide-react";
import { DocumentPreview } from "@/components/ui/DocumentPreview";
import DocumentPreviewDialog from "./DocumentPreviewDialog";

interface ApplicantDocument {
  id: string;
  applicant_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  category: string;
  description: string | null;
  uploaded_by: string;
  created_at: string;
  source_document_id?: string | null;
  page_range?: string | null;
  extracted_from_pdf?: boolean;
  is_profile_photo?: boolean;
}

interface ApplicantDocumentsProps {
  applicantId: string;
}

const DOCUMENT_CATEGORIES = [
  { value: "cv", label: "Lebenslauf" },
  { value: "cover_letter", label: "Anschreiben" },
  { value: "certificate", label: "Zeugnis" },
  { value: "qualification", label: "Qualifikationsnachweis" },
  { value: "reference", label: "Referenz" },
  { value: "id_document", label: "Ausweisdokument" },
  { value: "photo", label: "Foto" },
  { value: "other", label: "Sonstiges" },
];

const ApplicantDocuments = ({ applicantId }: ApplicantDocumentsProps) => {
  const { user, userRole } = useAuth();
  // Only admins can delete applicant documents
  const canDelete = userRole === "admin";
  const [documents, setDocuments] = useState<ApplicantDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("cv");
  const [description, setDescription] = useState("");
  const [useAiAnalysis, setUseAiAnalysis] = useState(true);
  const [splitDocuments, setSplitDocuments] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<ApplicantDocument | null>(null);
  const [isDropzoneActive, setIsDropzoneActive] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, [applicantId]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from("applicant_documents")
        .select("*")
        .eq("applicant_id", applicantId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setDocuments((data as ApplicantDocument[]) || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Fehler beim Laden der Dokumente");
    } finally {
      setLoading(false);
    }
  };

  const uploadSingleFile = async (file: File) => {
    if (!user) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Datei zu groß. Maximale Größe: 10MB");
      return;
    }

    if (useAiAnalysis) {
      await handleAiUpload(file);
    } else {
      await handleStandardUpload(file);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    await uploadSingleFile(file);

    // Reset file input
    event.target.value = "";
  };

  const handleDropzoneDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading || analyzing) return;
    setIsDropzoneActive(true);
  };

  const handleDropzoneDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropzoneActive(false);
  };

  const handleDropzoneDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropzoneActive(false);

    if (uploading || analyzing) return;
    if (!user) return;

    const droppedFiles = Array.from(e.dataTransfer.files || []);
    for (const file of droppedFiles) {
      await uploadSingleFile(file);
    }
  };

  const handleStandardUpload = async (file: File) => {
    setUploading(true);

    try {
      // Generate unique file path
      const fileExt = file.name.split(".").pop();
      const fileName = `${applicantId}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("applicant-documents")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Check if this is an image that should be set as profile photo
      const isImage = file.type.startsWith("image/");
      const isProfilePhoto = isImage && (selectedCategory === "photo" || selectedCategory === "cv");

      // Save document record
      const { error: insertError } = await supabase
        .from("applicant_documents")
        .insert({
          applicant_id: applicantId,
          file_name: file.name,
          file_path: fileName,
          file_size: file.size,
          mime_type: file.type,
          category: isImage ? "photo" : selectedCategory,
          description: description || null,
          uploaded_by: user!.id,
          is_profile_photo: isProfilePhoto,
        });

      if (insertError) throw insertError;

      // If it's an image and no profile photo exists yet, set it as profile photo
      if (isImage) {
        await setAsProfilePhotoIfNeeded(fileName);
      }

      toast.success("Dokument erfolgreich hochgeladen");
      setDescription("");
      fetchDocuments();
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error("Fehler beim Hochladen des Dokuments");
    } finally {
      setUploading(false);
    }
  };

  const setAsProfilePhotoIfNeeded = async (filePath: string) => {
    try {
      // Check if applicant already has a profile image
      const { data: applicant, error: fetchError } = await supabase
        .from("applicants")
        .select("profile_image_url")
        .eq("id", applicantId)
        .single();

      if (fetchError) {
        console.error("Error fetching applicant:", fetchError);
        return;
      }

      // If no profile image exists, set this one (store path, not public URL)
      if (!applicant?.profile_image_url) {
        const { error: updateError } = await supabase
          .from("applicants")
          .update({ profile_image_url: filePath })
          .eq("id", applicantId);

        if (updateError) {
          console.error("Error updating profile image:", updateError);
        } else {
          toast.success("Profilbild automatisch gesetzt");
        }
      }
    } catch (error) {
      console.error("Error setting profile photo:", error);
    }
  };

  const handleAiUpload = async (file: File) => {
    setAnalyzing(true);

    try {
      const formData = new FormData();
      formData.append("files", file);
      formData.append("applicantId", applicantId);
      formData.append("userId", user!.id);
      formData.append("autoSave", "true");
      formData.append("splitDocuments", splitDocuments ? "true" : "false");

      const { data: functionData, error: functionError } = await supabase.functions.invoke(
        "parse-applicant-documents",
        {
          body: formData,
        }
      );

      if (functionError) throw functionError;

      if (!functionData.success) {
        throw new Error(functionData.error || "AI-Analyse fehlgeschlagen");
      }

      const savedCount = functionData.savedDocuments?.length || 0;
      const splitCount = functionData.splitResults?.successfulParts || 0;

      let message = `Dokument analysiert und gespeichert`;
      if (splitCount > 0) {
        message += `. ${splitCount} Teildokumente extrahiert.`;
      }

      // Check if any images were detected or extracted - the edge function now handles
      // setting the profile_image_url automatically, so we just need to notify the user
      if (functionData.detectedImages && functionData.detectedImages.length > 0) {
        const hasProfilePhoto = functionData.detectedImages.some((img: any) => img.isProfilePhoto);
        if (hasProfilePhoto) {
          console.log("Profile photo was detected and set by edge function");
        }
      }

      // For uploaded images, the edge function also handles setting profile photo automatically
      // We can also check savedDocuments for profile photos with file_path
      const savedDocs = functionData.savedDocuments || [];
      const profilePhotoDoc = savedDocs.find((doc: any) => doc.file_path);
      
      // If the edge function didn't set a profile photo but we have a saved image, try to set it
      if (file.type.startsWith("image/") && profilePhotoDoc?.file_path) {
        await setAsProfilePhotoIfNeeded(profilePhotoDoc.file_path);
      }

      toast.success(message);
      fetchDocuments();
    } catch (error) {
      console.error("Error with AI upload:", error);
      toast.error("Fehler bei der AI-Analyse");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDownload = async (document: ApplicantDocument) => {
    try {
      const { data, error } = await supabase.storage
        .from("applicant-documents")
        .download(document.file_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = document.file_name;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Fehler beim Herunterladen des Dokuments");
    }
  };

  const handleDelete = async (document: ApplicantDocument) => {
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("applicant-documents")
        .remove([document.file_path]);

      if (storageError) throw storageError;

      // Delete record
      const { error: dbError } = await supabase
        .from("applicant_documents")
        .delete()
        .eq("id", document.id);

      if (dbError) throw dbError;

      toast.success("Dokument gelöscht");
      fetchDocuments();
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Fehler beim Löschen des Dokuments");
    }
  };

  const handlePreview = (doc: ApplicantDocument) => {
    setSelectedDocument(doc);
    setPreviewOpen(true);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith("image/")) return <FileImage className="h-4 w-4" />;
    if (mimeType.includes("pdf")) return <FileText className="h-4 w-4" />;
    if (mimeType.includes("zip") || mimeType.includes("archive")) return <FileArchive className="h-4 w-4" />;
    return <File className="h-4 w-4" />;
  };

  const getCategoryLabel = (category: string) => {
    return DOCUMENT_CATEGORIES.find((c) => c.value === category)?.label || category;
  };

  const canPreview = (mimeType: string) => {
    return mimeType.includes("pdf") || mimeType.startsWith("image/");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Dokumente
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Upload Section */}
          <div className="border rounded-lg p-4 bg-muted/30">
            <h4 className="font-medium mb-4">Neues Dokument hochladen</h4>
            
            {/* AI Analysis Toggle */}
            <div className="flex items-center gap-4 mb-4 p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ai-analysis"
                  checked={useAiAnalysis}
                  onCheckedChange={(checked) => setUseAiAnalysis(checked as boolean)}
                />
                <Label htmlFor="ai-analysis" className="flex items-center gap-2 cursor-pointer">
                  <Sparkles className="h-4 w-4 text-primary" />
                  AI-Analyse aktivieren
                </Label>
              </div>
              {useAiAnalysis && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="split-docs"
                    checked={splitDocuments}
                    onCheckedChange={(checked) => setSplitDocuments(checked as boolean)}
                  />
                  <Label htmlFor="split-docs" className="flex items-center gap-2 cursor-pointer">
                    <SplitSquareVertical className="h-4 w-4" />
                    Multi-Dokument-PDFs aufteilen
                  </Label>
                </div>
              )}
            </div>

            {!useAiAnalysis && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="category">Kategorie</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOCUMENT_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="description">Beschreibung (optional)</Label>
                  <Input
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="z.B. Arbeitszeugnis Firma XY"
                  />
                </div>
              </div>
            )}

            {useAiAnalysis && (
              <p className="text-sm text-muted-foreground mb-4">
                Die AI analysiert das Dokument automatisch, erkennt den Dokumenttyp und extrahiert 
                Bewerberinformationen. Bei Multi-Dokument-PDFs werden die einzelnen Dokumente 
                automatisch getrennt und kategorisiert.
              </p>
            )}

            <div className="mt-4">
              <Label htmlFor="file-upload" className="cursor-pointer">
                <div
                  onDragOver={handleDropzoneDragOver}
                  onDragLeave={handleDropzoneDragLeave}
                  onDrop={handleDropzoneDrop}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDropzoneActive ? "border-primary bg-primary/10" : "hover:border-primary hover:bg-primary/5"
                  }`}
                >
                  {uploading || analyzing ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>{analyzing ? "AI analysiert..." : "Wird hochgeladen..."}</span>
                    </div>
                  ) : (
                    <>
                      {useAiAnalysis ? (
                        <Sparkles className="h-8 w-8 mx-auto text-primary mb-2" />
                      ) : (
                        <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      )}
                      <p className="text-sm text-muted-foreground">
                        {useAiAnalysis 
                          ? "Dokument für AI-Analyse hochladen" 
                          : "Klicken Sie hier oder ziehen Sie eine Datei hierher"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Maximale Größe: 10MB
                      </p>
                    </>
                  )}
                </div>
              </Label>
              <Input
                id="file-upload"
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                disabled={uploading || analyzing}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.zip,.rar"
              />
            </div>
          </div>

          {/* Documents List */}
          {documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Noch keine Dokumente vorhanden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datei</TableHead>
                    <TableHead>Kategorie</TableHead>
                    <TableHead>Größe</TableHead>
                    <TableHead>Hochgeladen</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <div 
                          className={`flex items-center gap-2 ${canPreview(doc.mime_type) ? "cursor-pointer hover:text-primary" : ""}`}
                          onClick={() => canPreview(doc.mime_type) && handlePreview(doc)}
                        >
                          {getFileIcon(doc.mime_type)}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{doc.file_name}</p>
                              {doc.is_profile_photo && (
                                <Badge variant="secondary" className="text-xs">
                                  <User className="h-3 w-3 mr-1" />
                                  Profilfoto
                                </Badge>
                              )}
                              {doc.extracted_from_pdf && (
                                <Badge variant="outline" className="text-xs">
                                  <SplitSquareVertical className="h-3 w-3 mr-1" />
                                  Extrahiert
                                </Badge>
                              )}
                            </div>
                            {doc.description && (
                              <p className="text-xs text-muted-foreground">
                                {doc.description}
                              </p>
                            )}
                            {doc.page_range && (
                              <p className="text-xs text-muted-foreground">
                                Seite {doc.page_range}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getCategoryLabel(doc.category)}</TableCell>
                      <TableCell>{formatFileSize(doc.file_size)}</TableCell>
                      <TableCell>
                        {new Date(doc.created_at).toLocaleDateString("de-DE")}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canPreview(doc.mime_type) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handlePreview(doc)}
                              title="Vorschau"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownload(doc)}
                            title="Herunterladen"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {canDelete && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="icon" title="Löschen">
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Dokument löschen?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Das Dokument "{doc.file_name}" wird unwiderruflich gelöscht.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(doc)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Löschen
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unified Document Preview - uses blob URLs for cross-browser compatibility */}
      <DocumentPreview
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        document={selectedDocument}
        bucketName="applicant-documents"
        allDocuments={documents.filter(d => canPreview(d.mime_type))}
        onDocumentChange={(doc) => setSelectedDocument(doc as ApplicantDocument)}
      />
    </>
  );
};

export default ApplicantDocuments;
